/**
 * custom.js — Chivio / Foxify
 * ─────────────────────────────────────────────────────────────────────────────
 * Entrance animations powered by GSAP + ScrollTrigger.
 *
 * Animations:
 *   1. Hero banner — cinematic line-wipe reveal (clip-path, left → right)
 *   2. Section headings — slide-up reveal on scroll
 *   3. Product cards — staggered fade + lift on scroll into view
 *   4. Image sections — clip-path wipe reveal on scroll
 *   5. Icon / trust rows — staggered pop-in
 */

(function () {
  'use strict';

  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  if (typeof gsap === 'undefined') {
    console.warn('[custom.js] GSAP not loaded — animations skipped');
    return;
  }

  gsap.registerPlugin(ScrollTrigger);

  function init() {
    setupHeroCinematic();
    setupHeadingReveals();
    setupProductCardReveals();
    setupImageReveals();
    setupIconRowReveals();
  }

  if (!document.documentElement.classList.contains('page-loading')) {
    init();
  } else {
    document.addEventListener('theme:load', init, { once: true });
    document.addEventListener('DOMContentLoaded', () => setTimeout(init, 600), { once: true });
  }

  // ── 0. SCROLLBAR WIDTH CALCULATOR (For preventing layout jumps) ────────────
  function updateScrollbarWidth() {
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.documentElement.style.setProperty('--scrollbar-width', scrollbarWidth + 'px');
  }
  updateScrollbarWidth();
  window.addEventListener('resize', updateScrollbarWidth, { passive: true });

  // ── 1. HERO CINEMATIC WIPE ─────────────────────────────────────────────────
  function setupHeroCinematic() {
    const banner = document.querySelector(
      '.banner, .hero, .slideshow__slide--active, [class*="banner"]'
    );
    if (!banner) return;

    playBannerAnimation(banner);

    document.addEventListener('theme:slide:change', () => {
      const activeSlide = document.querySelector(
        '.slideshow__slide--active, .banner--active'
      );
      if (activeSlide) playBannerAnimation(activeSlide);
    });
  }

  function playBannerAnimation(container) {
    const heading = container.querySelector(
      '.banner__heading, .hero__heading, h1, h2'
    );
    const subtextEl = container.querySelector(
      '.banner__subtext, .hero__subtext, .banner__text p, .rte p'
    );
    const buttons = container.querySelectorAll(
      '.banner__buttons .btn, .hero__buttons .btn, .banner__button, .btn'
    );

    if (!heading) return;

    const originalHTML = heading.innerHTML;
    const text = heading.innerText.trim();
    const words = text.split(/\s+/);

    heading.innerHTML = words.map(w =>
      `<span class="chivio-line-mask" style="display:inline-block; overflow:hidden; vertical-align:bottom; margin-right:0.22em;">` +
      `<span class="chivio-line-inner" style="display:inline-block;">${w}</span>` +
      `</span>`
    ).join('');

    const lineInners = heading.querySelectorAll('.chivio-line-inner');

    gsap.killTweensOf([lineInners, subtextEl, buttons]);

    gsap.set(lineInners, { x: '-105%' });
    if (subtextEl) gsap.set(subtextEl, { opacity: 0, y: 16 });
    if (buttons.length) gsap.set(buttons, { opacity: 0, y: 14 });

    const tl = gsap.timeline({ delay: 0.25 });

    tl.to(lineInners, {
      x: '0%',
      duration: 1.0,
      ease: 'power4.out',
      stagger: 0.12,
    });

    if (subtextEl) {
      tl.to(subtextEl, {
        opacity: 1, y: 0,
        duration: 0.7, ease: 'power2.out',
      }, '-=0.55');
    }

    if (buttons.length) {
      tl.to(buttons, {
        opacity: 1, y: 0,
        duration: 0.55, ease: 'power2.out',
        stagger: 0.1,
      }, '-=0.45');
    }

    window.addEventListener('resize', () => {
      heading.innerHTML = originalHTML;
    }, { once: true });
  }

  // ── 2. SECTION HEADING REVEALS ─────────────────────────────────────────────
  function setupHeadingReveals() {
    const headings = document.querySelectorAll(
      '.shopify-section:not(:first-of-type) h2, ' +
      '.shopify-section:not(:first-of-type) h3, ' +
      '.shopify-section:not(:first-of-type) .section__heading, ' +
      '.shopify-section:not(:first-of-type) .section-title'
    );

    headings.forEach((el) => {
      if (el.closest('scroll-reveal-text')) return;

      gsap.fromTo(el,
        { opacity: 0, y: 36, clipPath: 'inset(0 0 100% 0)' },
        {
          opacity: 1, y: 0, clipPath: 'inset(0 0 0% 0)',
          duration: 0.85, ease: 'power3.out',
          scrollTrigger: {
            trigger: el,
            start: 'top 88%',
            toggleActions: 'play none none none',
          },
        }
      );
    });
  }

  // ── 3. PRODUCT CARD REVEALS ────────────────────────────────────────────────
  function setupProductCardReveals() {
    const grids = document.querySelectorAll(
      '.product-grid, .collection-grid, ' +
      '[class*="product-grid"], .featured-collection__grid'
    );

    grids.forEach((grid) => {
      const cards = grid.querySelectorAll(
        '.product-grid-item, .grid__item, .product-card, [class*="product-item"]'
      );
      if (!cards.length) return;

      gsap.fromTo(cards,
        { opacity: 0, y: 48, scale: 0.97 },
        {
          opacity: 1, y: 0, scale: 1,
          duration: 0.7, ease: 'power2.out',
          stagger: { amount: 0.45, from: 'start' },
          scrollTrigger: {
            trigger: grid,
            start: 'top 85%',
            toggleActions: 'play none none none',
          },
        }
      );
    });
  }

  // ── 4. IMAGE CLIP-PATH REVEALS ─────────────────────────────────────────────
  function setupImageReveals() {
    const imageWrappers = document.querySelectorAll(
      '.shopify-section:not(:first-of-type) .image-with-text__media, ' +
      '.shopify-section:not(:first-of-type) .section-image__wrapper, ' +
      '.shopify-section:not(:first-of-type) .overlapping-images__item'
    );

    imageWrappers.forEach((el) => {
      if (el.closest('parallax-element')) return;

      gsap.fromTo(el,
        { clipPath: 'inset(0 100% 0 0)', opacity: 0.6 },
        {
          clipPath: 'inset(0 0% 0 0)', opacity: 1,
          duration: 1.1, ease: 'power3.inOut',
          scrollTrigger: {
            trigger: el,
            start: 'top 80%',
            toggleActions: 'play none none none',
          },
        }
      );
    });
  }

  // ── 5. ICON / TRUST ROW REVEALS ────────────────────────────────────────────
  function setupIconRowReveals() {
    const iconSections = document.querySelectorAll(
      '.icons-row, .press__logos, .section-icons-row, [class*="icons-row"]'
    );

    iconSections.forEach((section) => {
      const items = section.querySelectorAll(
        '.icons-row__item, .press__logo, li, .grid__item'
      );
      if (!items.length) return;

      gsap.fromTo(items,
        { opacity: 0, y: 20, scale: 0.92 },
        {
          opacity: 1, y: 0, scale: 1,
          duration: 0.5, ease: 'back.out(1.4)',
          stagger: 0.07,
          scrollTrigger: {
            trigger: section,
            start: 'top 88%',
            toggleActions: 'play none none none',
          },
        }
      );
    });
  }

})();


/* ─── LAYOUT RECOVERY: bfcache + product-info swap ───────────────────────────
   Mobile browsers aggressively bfcache pages. When the user returns to the
   PDP after a cart add/remove, GSAP ScrollTrigger and Flickity have stale
   cached dimensions — the gallery collapses to zero width and the product
   details fill the page in a narrow left-aligned column.

   Fix: on pageshow (bfcache restore) and after any product-info DOM swap,
   refresh ScrollTrigger and dispatch a resize so Flickity re-measures.
   ─────────────────────────────────────────────────────────────────────────── */
(function () {
  'use strict';

  function refreshLayout() {
    // Delay two frames so the browser has fully laid out the DOM
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        window.dispatchEvent(new Event('resize'));
        if (typeof ScrollTrigger !== 'undefined' && ScrollTrigger.refresh) {
          ScrollTrigger.refresh();
        }
      });
    });
  }

  // bfcache restore — `persisted` is true when the page came from cache
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) {
      refreshLayout();
    }
  });

  // Also refresh after any product-info element is swapped into the DOM
  // (covers sibling-switcher, cart drawer updates, etc.)
  var layoutObs = new MutationObserver(function (muts) {
    for (var i = 0; i < muts.length; i++) {
      var added = muts[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        var node = added[j];
        if (node.nodeType !== 1) continue;
        if (node.matches && node.matches('product-info[id^="MainProduct-"]')) {
          refreshLayout();
          return;
        }
      }
    }
  });
  layoutObs.observe(document.body, { childList: true, subtree: true });
})();


/* ─── PDP GALLERY DOT PAGINATION (DESKTOP + MOBILE) ──────────────────────────
   Shows a row of dots beneath every PDP gallery, every breakpoint. Active dot
   stretches into a small pill. Tap a dot to jump. Hooks into Foxify's Swiper
   instance (exposed at .mainSlider.mainSwiper on the carousel component).

   Dots only render when the gallery has more than one slide.
   ─────────────────────────────────────────────────────────────────────────── */
(function () {
  'use strict';

  let initRetries = 0;

  function findGallery() {
    return document.querySelector('f-media-gallery, x-media-gallery, .product-gallery');
  }

  function findSwiper(gallery) {
    if (!gallery) return null;
    // Foxify Swiper path
    const carousel = gallery.querySelector('f-swiper-carousel-component, x-carousel-component');
    if (carousel && carousel.mainSwiper) return carousel.mainSwiper;
    const sw = gallery.querySelector('.swiper');
    if (sw && sw.swiper) return sw.swiper;
    // Flickity path — the actual PDP gallery uses Flickity, not Swiper
    const flickEl = gallery.querySelector('.product-gallery__media-slider.flickity-enabled, [data-flickity]');
    if (!flickEl) return null;
    if (window.Flickity) {
      const inst = window.Flickity.data(flickEl);
      if (inst) {
        // Adapt Flickity to the Swiper-like API we use below.
        // realIndex is exposed via a getter so it always returns the
        // current selectedIndex, not the value at adapter-creation time.
        const adapter = {
          slides: inst.cells.map(function (c) { return c.element; }),
          slideTo: function (i) { inst.select(i); },
          on: function (event, handler) {
            if (event === 'slideChange') {
              inst.on('select', handler);
            }
          }
        };
        Object.defineProperty(adapter, 'realIndex', {
          get: function () { return inst.selectedIndex; }
        });
        return adapter;
      }
    }
    // DOM-level fallback — theme.js bundles Flickity privately, so
    // window.Flickity never exists on this store and the branch above
    // can't run. Drive navigation through the (CSS-hidden but live)
    // prev/next buttons and read state off the is-selected class.
    const slides = Array.prototype.slice.call(
      flickEl.querySelectorAll('.product-gallery__media-slide')
    );
    if (slides.length < 2) return null;
    const nextBtn = flickEl.querySelector('.flickity-prev-next-button.next');
    const prevBtn = flickEl.querySelector('.flickity-prev-next-button.previous');
    if (!nextBtn || !prevBtn) return null;
    const currentIndex = function () {
      for (let i = 0; i < slides.length; i++) {
        if (slides[i].classList.contains('is-selected')) return i;
      }
      return 0;
    };
    const adapter = {
      slides: slides,
      slideTo: function (i) {
        const cur = currentIndex();
        const btn = i > cur ? nextBtn : prevBtn;
        const steps = Math.abs(i - cur);
        for (let k = 0; k < steps; k++) btn.click();
      },
      on: function (event, handler) {
        if (event !== 'slideChange') return;
        const mo = new MutationObserver(handler);
        slides.forEach(function (s) {
          mo.observe(s, { attributes: true, attributeFilter: ['class'] });
        });
      }
    };
    Object.defineProperty(adapter, 'realIndex', { get: currentIndex });
    return adapter;
  }

  function buildDots(gallery, swiper) {
    if (gallery.querySelector('.chivio-dot-pagination')) return;
    const slides = swiper.slides;
    if (!slides || slides.length < 2) return;

    const dots = document.createElement('div');
    dots.className = 'chivio-dot-pagination';
    dots.setAttribute('role', 'tablist');
    dots.setAttribute('aria-label', 'Product images');

    const dotEls = [];
    for (let i = 0; i < slides.length; i++) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'chivio-dot';
      dot.setAttribute('aria-label', 'Go to image ' + (i + 1));
      dot.dataset.index = i;
      dot.addEventListener('click', function () {
        try { swiper.slideTo(i); } catch (e) { /* no-op */ }
      });
      dots.appendChild(dot);
      dotEls.push(dot);
    }

    gallery.appendChild(dots);

    function updateActive() {
      const idx = (typeof swiper.realIndex === 'number') ? swiper.realIndex : 0;
      dotEls.forEach(function (d, i) {
        d.classList.toggle('is-active', i === idx);
        d.setAttribute('aria-selected', i === idx ? 'true' : 'false');
      });
    }

    swiper.on('slideChange', updateActive);
    updateActive();
  }

  function clearDots() {
    document.querySelectorAll('.chivio-dot-pagination').forEach(function (d) { d.remove(); });
  }

  function init() {
    const gallery = findGallery();
    if (!gallery) {
      // After a sibling swap the gallery can be momentarily absent (mid
      // view-transition). Retry instead of giving up — otherwise reset()
      // clears the dots and they never rebuild (the colour-change bug).
      if (++initRetries < 40) setTimeout(init, 100);
      return;
    }

    const swiper = findSwiper(gallery);
    if (!swiper) {
      // Foxify spins up Flickity async — retry up to ~4s
      if (++initRetries < 40) setTimeout(init, 100);
      return;
    }

    initRetries = 0;
    buildDots(gallery, swiper);
  }

  function reset() {
    clearDots();
    initRetries = 0;
    init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init after sibling-switcher AJAX swap. The switcher replaces the
  // element matched by `[id^="productTemplate--"], [id^="MainProduct-"]` —
  // which on this theme is the productTemplate-- wrapper, NOT a
  // product-info[id^="MainProduct-"]. The old observer only matched the
  // latter, so it never fired and the dots never rebuilt after a colour
  // change. Broaden it to any product section/gallery being swapped in.
  function isProductSwap(node) {
    if (node.nodeType !== 1 || !node.matches) return false;
    return (
      node.matches('product-info[id^="MainProduct-"]') ||
      node.matches('[id^="productTemplate--"]') ||
      node.matches('[id^="MainProduct-"]') ||
      (node.querySelector && !!node.querySelector('.product-gallery'))
    );
  }
  const obs = new MutationObserver(function (muts) {
    for (let i = 0; i < muts.length; i++) {
      const added = muts[i].addedNodes;
      for (let j = 0; j < added.length; j++) {
        if (isProductSwap(added[j])) {
          // init() already retries for ~3s until Flickity is ready, so a
          // single reset() after the swap settles is enough.
          requestAnimationFrame(reset);
          return;
        }
      }
    }
  });
  obs.observe(document.body, { childList: true, subtree: true });
})();


/* ─── PDP SWATCH → SIZE BUTTON COLOUR ACCENT ─────────────────────────────────
   When a sibling colour is selected (aria-current="true"), the currently
   checked size button takes that colour as its fill. Reads the colour from
   `data-sibling-color` (added by snippets/product-siblings.liquid). Re-runs
   on every size change AND after sibling-switcher's AJAX swap (since the
   product-info element gets replaced wholesale).
   ─────────────────────────────────────────────────────────────────────────── */
(function () {
  'use strict';

  const ACTIVE_CLASS = 'chivio-size-active';

  function activeSwatchColour() {
    // Try several selectors — Foxify can render the "current" sibling
    // with different markers depending on settings.
    const candidates = [
      '.siblings a[aria-current="true"]',
      '.siblings__swatch[aria-current="true"]',
      '.siblings a.is-active',
      '.siblings__swatch.is-active',
      '.siblings a[data-sibling-color][aria-current]',
    ];
    let swatch = null;
    for (let i = 0; i < candidates.length && !swatch; i++) {
      swatch = document.querySelector(candidates[i]);
    }
    if (!swatch) {
      // Fallback: pick the swatch matching the current pathname.
      const here = window.location.pathname.replace(/\/$/, '');
      const all = document.querySelectorAll('.siblings a[href]');
      for (let i = 0; i < all.length; i++) {
        const path = (all[i].getAttribute('href') || '').replace(/\/$/, '');
        if (path && here.endsWith(path)) { swatch = all[i]; break; }
      }
    }
    if (!swatch) return '';

    // Read colour from data-sibling-color, or fall back to the inline
    // --swatch CSS variable on the link / its child.
    const direct = (swatch.getAttribute('data-sibling-color') || '').trim();
    if (direct) return direct;
    const inlineSwatch = (swatch.style.getPropertyValue('--swatch') || '').trim();
    if (inlineSwatch) return inlineSwatch;
    const childWithSwatch = swatch.querySelector('[style*="--swatch"]');
    if (childWithSwatch) {
      const val = childWithSwatch.style.getPropertyValue('--swatch').trim();
      if (val) return val;
    }
    return '';
  }

  function findSizeLabel(input) {
    if (input.nextElementSibling && input.nextElementSibling.tagName === 'LABEL') {
      return input.nextElementSibling;
    }
    if (input.id) {
      const lbl = document.querySelector('label[for="' + CSS.escape(input.id) + '"]');
      if (lbl) return lbl;
    }
    if (input.parentElement) {
      return input.parentElement.querySelector('label, .radio__button, .swatch__button');
    }
    return null;
  }

  function isSizeInput(input) {
    if (!input || input.type !== 'radio') return false;
    const name = (input.getAttribute('name') || '').toLowerCase();
    return name.indexOf('size') !== -1 || name.indexOf('ize') !== -1;
  }

  function applyAccent() {
    const colour = activeSwatchColour();

    // Set --chivio-active-color on:
    //  1) <html> — cascades to everything, guaranteed reach
    //  2) every product-info — survives sibling-switcher swaps which
    //     replace the entire <product-info> node and may strip <html>
    //     attrs depending on browser timing
    //  3) the .radio__fieldset wrapper — strongest specificity match
    //     for CSS rule `.radio__fieldset .radio__input:checked ~ .radio__label`
    const setOrClear = function (el) {
      if (!el) return;
      if (colour) el.style.setProperty('--chivio-active-color', colour);
      else el.style.removeProperty('--chivio-active-color');
    };

    setOrClear(document.documentElement);
    document.querySelectorAll('[id^="MainProduct-"], [id^="productTemplate--"]').forEach(setOrClear);
    document.querySelectorAll('fieldset, .radio__fieldset, .radio__buttons').forEach(function (fs) {
      const hasSize = !!fs.querySelector('input[type="radio"][name*="ize" i]');
      if (!hasSize) return;
      setOrClear(fs);
    });

    // Strip any leftover inline styles on labels from the prior
    // JS-driven implementation (paint-by-:checked-CSS now).
    document.querySelectorAll('input[type="radio"]').forEach(function (input) {
      if (!isSizeInput(input)) return;
      const label = findSizeLabel(input);
      if (!label) return;
      label.style.removeProperty('background');
      label.style.removeProperty('border-color');
      label.style.removeProperty('color');
      if (input.checked) label.classList.add(ACTIVE_CLASS);
      else label.classList.remove(ACTIVE_CLASS);
    });
  }

  // Run on size-radio change
  document.addEventListener('change', function (e) {
    if (isSizeInput(e.target)) applyAccent();
  });

  // Initial pass
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', applyAccent);
  } else {
    applyAccent();
  }

  // Re-apply after sibling-switcher AJAX swap (replaces the entire
  // product-info element, wiping our inline styles + class).
  const obs = new MutationObserver(function (muts) {
    for (let i = 0; i < muts.length; i++) {
      const added = muts[i].addedNodes;
      for (let j = 0; j < added.length; j++) {
        const node = added[j];
        if (node.nodeType !== 1) continue;
        if (node.matches && (node.matches('product-info[id^="MainProduct-"]') || node.matches('section[id^="productTemplate--"]'))) {
          // Wait one frame for child elements to settle, then apply
          requestAnimationFrame(applyAccent);
          return;
        }
      }
    }
  });
  obs.observe(document.body, { childList: true, subtree: true });
})();


/* === CHIVIO — Empty cart choreography (Bundle 6) ===
   Spec: 1,480ms entrance, bag stroke-draw + word-rise heading + fade-up
   eyebrow/body/CTA. Reduced-motion = opacity-only ~940ms. Revisits in
   the same session play a trimmed ~720ms version.
   Replaces Foxify's default empty-cart copy on the /cart page only;
   leaves the cart drawer's empty state untouched. */
(function () {
  const SESS_KEY = 'chivio.cartEmpty.lastSeen';

  function mountAll() {
    // Mount on the cart page if present
    if (document.body.classList.contains('template-cart')) {
      const pageEmpty = document.querySelector('.cart__empty:not(.hidden)');
      if (pageEmpty) mountInto(pageEmpty, 'page');
    }
    // Mount inside the cart drawer if it's showing the empty state
    const drawerEmpty = document.querySelector(
      '.cart-drawer__empty-message:not(.hidden), ' +
      '[data-empty-message]:not(.hidden)'
    );
    if (drawerEmpty && drawerEmpty.closest('.cart-drawer, cart-element#cart-drawer')) {
      mountInto(drawerEmpty, 'drawer');
    }
  }

  function init() {
    mountAll();
    // Re-mount whenever the cart changes (item added/removed)
    document.addEventListener('theme:cart:added', mountAll);
    document.addEventListener('theme:cart:change', mountAll);
    // Also observe DOM mutations on the drawer so we catch open/close cycles
    const drawer = document.getElementById('cart-drawer');
    if (drawer) {
      const obs = new MutationObserver(function () { mountAll(); });
      obs.observe(drawer, { childList: true, subtree: true });
    }
  }

  function mountInto(empty, mode) {
    if (!empty) return;
    if (empty.querySelector('.chivio-empty')) return; // already mounted

    const wrap = document.createElement('div');
    wrap.className = 'chivio-empty chivio-empty--' + (mode || 'page');
    wrap.innerHTML = [
      '<div class="chivio-empty__bag" aria-hidden="true">',
        '<div class="chivio-empty__lottie" data-chivio-lottie-host></div>',
      '</div>',
      '<p class="chivio-empty__eyebrow">Your bag</p>',
      '<h2 class="chivio-empty__heading" aria-label="Nothing here yet.">',
        '<span class="word w1"><span>Nothing</span></span> ',
        '<span class="word w2"><span>here</span></span> ',
        '<span class="word w3"><span>yet.</span></span>',
      '</h2>',
      '<p class="chivio-empty__body">A blank bag is a small invitation. Begin with one piece — a tee in a colour you’ll wear weekly, or a polo cut for the long arc.</p>',
      '<a class="chivio-empty__cta" href="/collections/all">',
        'Continue shopping',
        '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">',
          '<path d="M5 12h14"/><path d="m13 6 6 6-6 6"/>',
        '</svg>',
      '</a>'
    ].join('');

    // Insert at top of .cart__empty (keeps Foxify's collections grid below)
    empty.insertBefore(wrap, empty.firstChild);

    // Load lottie-web on demand and mount the empty-cart animation
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let lottieAnim = null;
    function mountLottie() {
      if (!window.lottie) return;
      const container = wrap.querySelector('[data-chivio-lottie-host]');
      if (!container) return;
      // Resolve lottie URL: prefer the cart.liquid data attr, then any
      // global data attr on body, then a window var, then sensible fallback
      const lottiePath =
        (empty && empty.dataset && empty.dataset.chivioEmptyLottie) ||
        (document.body.dataset && document.body.dataset.chivioEmptyLottie) ||
        window.CHIVIO_EMPTY_LOTTIE_URL ||
        '/cdn/shop/files/empty-cart.json';
      lottieAnim = window.lottie.loadAnimation({
        container: container,
        renderer: 'svg',
        loop: true,
        autoplay: !reduce,
        path: lottiePath
      });
      if (reduce) {
        lottieAnim.addEventListener('DOMLoaded', function () {
          lottieAnim.goToAndStop(0, true);
        });
      }
      document.addEventListener('visibilitychange', function () {
        if (!lottieAnim) return;
        if (document.hidden) lottieAnim.pause();
        else if (!reduce) lottieAnim.play();
      });
    }

    if (window.lottie) {
      mountLottie();
    } else if (!document.querySelector('script[data-chivio-lottie]')) {
      const s = document.createElement('script');
      s.src = 'https://unpkg.com/lottie-web@5.12.2/build/player/lottie_light.min.js';
      s.async = true;
      s.dataset.chivioLottie = '1';
      s.onload = mountLottie;
      document.head.appendChild(s);
    }

    // Mode: revisit if we've been here this session
    const isRevisit = sessionStorage.getItem(SESS_KEY) === '1';
    if (isRevisit) wrap.classList.add('is-revisit');
    try { sessionStorage.setItem(SESS_KEY, '1'); } catch (e) {}

    // Trigger entrance next frame (lets browser apply armed state first)
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        wrap.classList.add('is-playing');
      });
    });

    // Fade in Foxify's empty-collections grid via IntersectionObserver
    // (or immediately if grid is already in view on load)
    const grid = empty.querySelector('.cart-empty-collections, [class*="cart-empty-collection"]');
    if (grid) {
      const reveal = function () { empty.classList.add('is-grid-in'); };
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              setTimeout(reveal, 1100); // tail just after CTA arrives
              io.disconnect();
            }
          });
        }, { threshold: 0.15 });
        io.observe(grid);
      } else {
        setTimeout(reveal, 1100);
      }
    }

    // Pause ambient bag breath when tab is hidden
    document.addEventListener('visibilitychange', function () {
      const bag = wrap.querySelector('.chivio-empty__bag');
      if (!bag) return;
      bag.style.animationPlayState = document.hidden ? 'paused' : 'running';
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();


/* === CHIVIO — Cursor system =========================================
   Two cursors working together:

   1) Site-wide: a tiny dot + lagging outline ring follow the mouse on
      every page. Over interactive elements the ring grows. Subtle.

   2) PDP gallery only: the dot+ring hide and a bigger black circle
      with directional arrow takes over. Click advances slides.

   Touch / coarse-pointer devices are skipped — native cursor stays. */
(function () {
  if (typeof window === 'undefined') return;
  if (!window.matchMedia) return;
  if (window.matchMedia('(hover: none)').matches) return;
  if (!window.matchMedia('(pointer: fine)').matches) return;

  /* ---------- Site-wide chalk-stroke cursor ---------- */
  const mark = document.createElement('div');
  mark.className = 'chivio-cursor-mark';
  mark.setAttribute('aria-hidden', 'true');
  document.body.appendChild(mark);

  /* ---------- PDP gallery cursor ---------- */
  const galleryCursor = document.createElement('div');
  galleryCursor.className = 'chivio-cursor';
  galleryCursor.setAttribute('aria-hidden', 'true');
  galleryCursor.innerHTML =
    '<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">' +
      '<path d="M5 12h14"/>' +
      '<path d="m13 6 6 6-6 6"/>' +
    '</svg>';
  document.body.appendChild(galleryCursor);

  /* ---------- State ---------- */
  let mouseX = -100, mouseY = -100;
  let markVisible = false, galleryVisible = false;
  let isPrev = false;
  let inGallery = false;

  function findGallery(el) {
    if (!document.body.classList.contains('template-product')) return null;
    if (!el || !el.closest) return null;
    return el.closest(
      '.product-gallery__media-slider, .product-gallery, .product-single__photos'
    );
  }

  function isInteractive(el) {
    if (!el || !el.closest) return false;
    return !!el.closest(
      'a, button, summary, [role="button"], label[for], ' +
      '.siblings a, .radio__label, [data-tooltip], .product__submit__add'
    );
  }

  /* ---------- Mousemove ---------- */
  document.addEventListener('mousemove', function (e) {
    mouseX = e.clientX;
    mouseY = e.clientY;

    const gallery = findGallery(e.target);
    inGallery = !!gallery;

    if (inGallery) {
      // Hide site-wide chalk; show gallery cursor
      if (markVisible) { markVisible = false; mark.classList.remove('is-visible'); }

      galleryCursor.style.left = mouseX + 'px';
      galleryCursor.style.top = mouseY + 'px';
      if (!galleryVisible) {
        galleryVisible = true;
        galleryCursor.classList.add('is-visible');
      }
      const rect = gallery.getBoundingClientRect();
      const prev = mouseX < rect.left + rect.width / 2;
      if (prev !== isPrev) {
        isPrev = prev;
        galleryCursor.classList.toggle('is-prev', prev);
      }
      return;
    }

    // Outside gallery — show site-wide chalk, hide gallery cursor
    if (galleryVisible) { galleryVisible = false; galleryCursor.classList.remove('is-visible'); }
    mark.style.left = mouseX + 'px';
    mark.style.top = mouseY + 'px';
    if (!markVisible) {
      markVisible = true;
      mark.classList.add('is-visible');
    }
    const active = isInteractive(e.target);
    mark.classList.toggle('is-active', active);
  }, { passive: true });

  /* ---------- Leave window ---------- */
  document.addEventListener('mouseleave', function () {
    markVisible = galleryVisible = false;
    mark.classList.remove('is-visible');
    galleryCursor.classList.remove('is-visible');
  });

  /* ---------- Press feedback ---------- */
  document.addEventListener('mousedown', function () {
    if (inGallery) galleryCursor.classList.add('is-down');
    else mark.classList.add('is-down');
  });
  document.addEventListener('mouseup', function () {
    galleryCursor.classList.remove('is-down');
    mark.classList.remove('is-down');
  });

  /* ---------- Gallery click → advance slide ---------- */
  document.addEventListener('click', function (e) {
    const gallery = findGallery(e.target);
    if (!gallery) return;
    if (e.target.closest('button, a, [data-zoom], [data-tooltip]')) return;
    const selector = isPrev
      ? '.flickity-prev-next-button.previous'
      : '.flickity-prev-next-button.next';
    const btn = gallery.querySelector(selector);
    if (btn) btn.click();
  });
})();


/* === CHIVIO — Collection page editorial meta (Bundle 16) =============
   On any /collections/* page, find the section-text block that holds
   the collection title and inject a meta block beneath it:
     - one-line editorial subhead (per-collection or fallback)
     - product count in small caps tracked
   Looks in the DOM only, no liquid edit. */
(function () {
  if (!document.body.classList.contains('template-collection')) return;

  const SUBHEADS = {
    all:           'Built to wear weekly. Cut for the long arc.',
    'classic-tee': '100% Supima cotton. Invisible stitching. Modern cut.',
    'essential-polo': 'Pique knit. Considered placket. Built to last.',
    tees:          'The everyday tee, considered.',
    polos:         'The everyday polo, considered.'
  };

  function buildMeta() {
    const titleHost = document.querySelector(
      '.template-collection .section-text [class*="heading"], ' +
      '.template-collection .section-text h1, ' +
      '.template-collection .section-text h2'
    );
    if (!titleHost) return;
    if (document.querySelector('.chivio-collection-meta')) return; // already injected

    // Read collection handle from URL
    const path = window.location.pathname || '';
    const m = path.match(/\/collections\/([^/?]+)/);
    const handle = m ? m[1] : 'all';
    const subhead = SUBHEADS[handle] || SUBHEADS.all;

    // Try to read product count from the rendered grid
    const countNode = document.querySelector(
      '[data-products-count], .collection__count, .collection-count'
    );
    let count = '';
    if (countNode) {
      count = countNode.textContent.trim();
    } else {
      const items = document.querySelectorAll('.product-grid-item');
      if (items.length > 0) {
        count = items.length + ' piece' + (items.length === 1 ? '' : 's');
      }
    }

    const meta = document.createElement('div');
    meta.className = 'chivio-collection-meta';
    meta.innerHTML =
      '<p class="chivio-collection-meta__line">' + subhead + '</p>' +
      (count ? '<p class="chivio-collection-meta__count">' + count + '</p>' : '');

    // Insert immediately after the title host's parent block
    const insertAfter = titleHost.closest('.section-text-block, .product__block') || titleHost;
    insertAfter.parentNode.insertBefore(meta, insertAfter.nextSibling);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', buildMeta);
  } else {
    buildMeta();
  }

  // Re-run after pagination / infinity-scroll re-renders the grid
  const obs = new MutationObserver(function () {
    buildMeta();
  });
  obs.observe(document.body, { childList: true, subtree: true });
})();


/* === CHIVIO — PDP video strip ========================================
   The product templates carry one slideshow section whose slides are
   background videos. Client wants them as individual, simultaneously
   playing sections in a side-scrolling strip (all four visible on
   desktop, swipeable cards on mobile) instead of a one-at-a-time
   carousel.

   We destroy the slideshow's Flickity instance (exposed at .flkty on
   the slideshow-component; Flickity.destroy() restores the original
   DOM and its methods guard on isActive, so the theme's lingering
   resize handlers are safe) and add .chivio-video-strip, which the CSS
   turns into a scroll-snap flex strip. Each slide's <video-background>
   mounts and autoplays itself via its own IntersectionObserver the
   moment its tile scrolls into view — no extra playback wiring needed. */
(function () {
  if (!document.body.classList.contains('template-product')) return;

  let tries = 0;

  function convert() {
    const sliders = document.querySelectorAll('slideshow-component');
    let pendingFlkty = false;

    sliders.forEach(function (ss) {
      if (ss.classList.contains('chivio-video-strip')) return;
      // Only slideshows whose slides are videos
      if (!ss.querySelector('video-background, .video__container')) return;

      if (!ss.flkty) {
        pendingFlkty = true; // component not initialized yet — retry
        return;
      }
      try { ss.flkty.destroy(); } catch (e) { /* already destroyed */ }
      ss.classList.add('chivio-video-strip');

      // Nudge each video-background's IntersectionObserver — after the
      // DOM restructure some browsers need a resize to re-evaluate.
      window.dispatchEvent(new Event('resize'));
    });

    if (pendingFlkty && ++tries < 50) setTimeout(convert, 120);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', convert);
  } else {
    convert();
  }
  document.addEventListener('theme:load', convert, { once: true });
})();


/* === CHIVIO — PDP heading: colour-name → product line name ==========
   On some products the merchant typed a bare colour ("Crimson") as the
   first line of the Description, which the theme renders as the visual
   heading — so the Crimson tee read "Crimson" instead of "Classic Tee".
   The product's real title IS the line name (e.g. "Classic Tee"), passed
   in via data-product-title. When the description's first line is just a
   colour, replace it with the title in a clean bold heading (dropping any
   inline colour so it matches the other variants). Only touches bare
   colour-name first lines; anything else is left alone. Re-runs after the
   sibling-switcher AJAX swap. */
(function () {
  if (!document.body.classList.contains('template-product')) return;

  var COLOURS = [
    'crimson', 'sienna', 'sage', 'sapphire', 'space', 'black', 'white',
    'navy', 'navy blue', 'blue', 'olive', 'maroon', 'crew neck'
  ];

  function fixHeading(root) {
    var blocks = (root || document).querySelectorAll('.product__description[data-product-title]');
    blocks.forEach(function (d) {
      if (d.dataset.chivioHeadingFixed === '1') return;
      var title = (d.getAttribute('data-product-title') || '').trim();
      if (!title) return;
      var p = d.querySelector('p');
      if (!p) return;
      var txt = (p.textContent || '').replace(/ /g, ' ').trim().toLowerCase();
      if (COLOURS.indexOf(txt) === -1) return; // not a bare colour — leave it
      while (p.firstChild) p.removeChild(p.firstChild);
      var b = document.createElement('b');
      b.textContent = title;
      p.appendChild(b);
      d.dataset.chivioHeadingFixed = '1';
    });
  }

  // Collapse the empty Word "spacer" paragraph in the description to one
  // small consistent gap, so every colour variant matches (clean variants
  // had a ~46px empty <p> before the Colour line; fixed ones had ~0).
  function fixGap(root) {
    var blocks = (root || document).querySelectorAll('.product__description');
    blocks.forEach(function (d) {
      if (d.dataset.chivioGapFixed === '1') return;
      var ps = d.querySelectorAll('p');
      for (var i = 0; i < ps.length; i++) {
        var pp = ps[i];
        if ((pp.textContent || '').replace(/\s| /g, '') === '') {
          pp.style.setProperty('margin', '0', 'important');
          pp.style.setProperty('height', '11px', 'important');
          pp.style.setProperty('min-height', '0', 'important');
          pp.style.setProperty('line-height', '11px', 'important');
          pp.style.setProperty('font-size', '11px', 'important');
        }
      }
      d.dataset.chivioGapFixed = '1';
    });
  }

  function run() { fixHeading(); fixGap(); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }

  // Re-run after a sibling colour swap replaces the product-info DOM.
  var obs = new MutationObserver(function (muts) {
    for (var i = 0; i < muts.length; i++) {
      var added = muts[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        var n = added[j];
        if (n.nodeType !== 1) continue;
        if (n.matches && (n.matches('[id^="MainProduct-"]') || n.matches('[id^="productTemplate--"]') || (n.querySelector && n.querySelector('.product__description')))) {
          requestAnimationFrame(run);
          return;
        }
      }
    }
  });
  obs.observe(document.body, { childList: true, subtree: true });
})();


/* === CHIVIO — PDP header hide + top-left hot-zone reveal ============
   Hides the site header on the product page and reveals it when the
   cursor enters a top-left hot zone (240×100). Once revealed, the
   header's own :hover keeps it visible until the cursor moves below
   ~220px. CSS handles the slide animation; JS only toggles the body
   class. Skips on touch devices. */
(function () {
  if (!document.body.classList.contains('template-product')) return;
  if (window.matchMedia && window.matchMedia('(hover: none)').matches) return;

  const HOTZONE_X = 320;  // top-left corner width
  const HOTZONE_Y = 100;  // top-left corner height
  const HIDE_BELOW_Y = 220;  // hide the header again once cursor goes here

  let leaveTimer = null;

  function show() {
    document.body.classList.add('chivio-show-header');
    if (leaveTimer) { clearTimeout(leaveTimer); leaveTimer = null; }
  }
  function hide() {
    if (leaveTimer) return;
    leaveTimer = setTimeout(function () {
      document.body.classList.remove('chivio-show-header');
      leaveTimer = null;
    }, 320);
  }

  document.addEventListener('mousemove', function (e) {
    if (e.clientY < HOTZONE_Y) {
      show();
      return;
    }
    if (e.clientY > HIDE_BELOW_Y) {
      hide();
    }
  }, { passive: true });

  // Cart drawer or any drawer opening should pin the header so the user
  // doesn't lose context.
  ['focus', 'click'].forEach(function (evt) {
    document.addEventListener(evt, function (e) {
      if (e.target && e.target.closest && e.target.closest('.site-header')) show();
    }, true);
  });
})();


/* === CHIVIO — Cart icon always opens drawer (defensive) ==============
   Whichever theme setting is active (page vs drawer), force every cart-
   link click to open the drawer sidebar. Skips the /cart page itself
   (we don't want to break the page if user lands there directly). */
(function () {
  function getDrawer() {
    return document.getElementById('cart-drawer') ||
           document.querySelector('[data-cart-drawer]') ||
           document.querySelector('cart-element#cart-drawer');
  }
  function openDrawer() {
    const drawer = getDrawer();
    if (!drawer) return false;
    // Foxify's own API keeps its isCartDrawerOpen state in sync — manual
    // class flips desync the toggle and the close button needs two taps.
    if (typeof drawer.openCartDrawer === 'function') {
      try { drawer.openCartDrawer(); return true; } catch (e) { /* fall through */ }
    }
    document.body.classList.add('js-drawer-open-cart');
    drawer.setAttribute('aria-hidden', 'false');
    drawer.setAttribute('aria-expanded', 'true');
    return true;
  }

  document.addEventListener('click', function (e) {
    // Catch any cart link variant: /cart, /cart/, /cart?, /cart#,
    // and the explicit toggle classes/attributes
    const link = e.target.closest('a[href*="/cart"], .cart__toggle, [data-cart-toggle], .sm-cart');
    if (!link) return;

    // Native Foxify toggles handle themselves (preventDefault + toggle in
    // theme.js) — hijacking them double-fires the open and desyncs state.
    if (link.hasAttribute('data-cart-drawer-toggle')) return;

    // For href links, confirm it's the cart page (not /cart-policy, etc.)
    if (link.tagName === 'A') {
      const href = link.getAttribute('href') || '';
      // Normalise: strip query, fragment, trailing slash, then check
      const path = href.split('#')[0].split('?')[0].replace(/\/$/, '');
      if (!path.endsWith('/cart')) return;
    }

    // Already on the cart page → let the click do its thing
    if (document.body.classList.contains('template-cart')) return;
    // Don't hijack if there's no drawer in the DOM
    if (!getDrawer()) return;
    e.preventDefault();
    openDrawer();
  }, true);

  // ── OPTIMISTIC CART TOTALS (Instant qty feedback) ───────────────────────────
  // Foxify only repaints the line price + subtotal AFTER the /cart/change
  // round-trip (debounce + network, several seconds), so a shopper can't see
  // what "+1" costs until it lands. We repaint the affected line total and the
  // cart subtotal IMMEDIATELY from each line's data-unit-price (cents, stamped
  // in cart-line-items.liquid). The server response reconciles to the same
  // number afterwards, so there's no flicker — just instant feedback.

  // Format `cents` to a money string that mirrors `sample`'s currency prefix
  // and decimal style (e.g. "Rs. 1,799.00" → "Rs. 3,598.00"). Indian grouping.
  function chvFormatMoneyLike(cents, sample) {
    const s = sample || '';
    const decimals = /\.\d{2}(\D|$)/.test(s) ? 2 : 0;
    const num = new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(cents / 100);
    const prefix = (s.match(/^[^\d]*/) || [''])[0];
    return prefix + num;
  }

  function chvSetActiveLinePrice(priceEl, cents) {
    const ins = priceEl.querySelector('ins');
    if (ins) { ins.textContent = chvFormatMoneyLike(cents, ins.textContent); return; }
    // Not on sale — the active price is the element's leading text node.
    for (let i = 0; i < priceEl.childNodes.length; i++) {
      const n = priceEl.childNodes[i];
      if (n.nodeType === 3 && n.textContent.trim()) {
        n.textContent = chvFormatMoneyLike(cents, n.textContent);
        return;
      }
    }
  }

  // Recompute subtotal from every line's unit price × its quantity. For the
  // just-clicked item the input hasn't been updated by Foxify yet (we run in
  // capture phase), so its predicted qty is passed in as an override.
  function chvUpdateCartTotals(overrideItem, overrideQty) {
    let cents = 0;
    document.querySelectorAll('.cart__item').forEach(function (it) {
      if (it.hasAttribute('data-removing')) return;
      const unit = parseInt(it.getAttribute('data-unit-price'), 10) || 0;
      let q;
      if (it === overrideItem) {
        q = overrideQty;
      } else {
        const inp = it.querySelector('input[type="number"], [data-quantity-field]');
        q = inp ? (parseInt(inp.value, 10) || 0) : 0;
      }
      cents += unit * q;
    });
    document.querySelectorAll('[data-cart-total], [data-cart-total-price], .cart__total__money').forEach(function (el) {
      el.textContent = chvFormatMoneyLike(cents, el.textContent || 'Rs. 0.00');
    });
  }

  document.addEventListener('click', function (e) {
    const plus = e.target.closest('[data-quantity-plus]');
    const minus = e.target.closest('[data-quantity-minus]');
    if (!plus && !minus) return;
    const item = (plus || minus).closest('.cart__item');
    if (!item) return;
    const input = item.querySelector('input[type="number"], [data-quantity-field]');
    if (!input) return;

    const qty = parseInt(input.value, 10) || 1;
    const newQty = plus ? qty + 1 : qty - 1;
    if (newQty < 1) return; // qty→0 is a removal, handled by the block below

    // NOTE: we deliberately do NOT set input.value — Foxify's own handler
    // increments it; touching it here would double-count.
    const unit = parseInt(item.getAttribute('data-unit-price'), 10) || 0;
    const unitCompare = parseInt(item.getAttribute('data-unit-compare'), 10) || 0;
    const priceEl = item.querySelector('[data-line-price], .cart__item__price');
    if (priceEl && unit) {
      chvSetActiveLinePrice(priceEl, unit * newQty);
      const del = priceEl.querySelector('del');
      if (del && unitCompare > 0) {
        del.textContent = chvFormatMoneyLike(unitCompare * newQty, del.textContent);
      }
    }
    chvUpdateCartTotals(item, newQty);
  }, true);

  // -- INSTANT CART REMOVAL (Optimistic UI) --
  // Foxify takes 1-2s to process a cart removal via AJAX. To make it feel instant,
  // we immediately collapse the item visually. The server response will eventually
  // re-render the cart drawer with the final state.
  document.addEventListener('click', function(e) {
    const removeBtn = e.target.closest('.cart__item__remove');
    const minusBtn = e.target.closest('[data-quantity-minus]');

    let shouldHide = false;
    let itemEl = null;

    if (removeBtn) {
      shouldHide = true;
      itemEl = removeBtn.closest('.cart__item');
    } else if (minusBtn) {
      const input = minusBtn.parentElement.querySelector('input[type="number"]');
      if (input && parseInt(input.value) <= 1) {
        shouldHide = true;
        itemEl = minusBtn.closest('.cart__item');
      }
    }

    if (shouldHide && itemEl) {
      // Mark it so the optimistic subtotal recompute excludes it immediately.
      itemEl.setAttribute('data-removing', '1');
      // Use CSS transitions to animate it away smoothly before Foxify's AJAX finishes
      itemEl.style.transition = 'opacity 0.2s ease, max-height 0.2s ease, padding 0.2s ease, margin 0.2s ease';
      itemEl.style.opacity = '0';
      itemEl.style.maxHeight = '0px';
      itemEl.style.paddingTop = '0';
      itemEl.style.paddingBottom = '0';
      itemEl.style.marginTop = '0';
      itemEl.style.marginBottom = '0';
      itemEl.style.border = 'none';
      itemEl.style.overflow = 'hidden';
      itemEl.style.pointerEvents = 'none';
      // Drop this line from the subtotal right away too.
      chvUpdateCartTotals();
    }
  }, true);

})();
